import { useEffect, useState } from 'react';
import api from '../lib/api';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  FunnelChart, Funnel, LabelList, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar,
} from 'recharts';
import { STATUS_LABELS, PARTNERSHIP_TYPE_LABELS, fmtCurrency } from '../lib/utils';
import { HistoricalAnalytics } from '../components/dashboard/HistoricalAnalytics';

const NAVY = '#0E1944';
const MAGENTA = '#E91E8C';
const COLORS = [MAGENTA, NAVY, '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#06B6D4', '#EF4444'];

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [byType, setByType] = useState(null);
  const [followups, setFollowups] = useState(null);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/analytics/charts'),
      api.get('/analytics/by-partnership-type'),
      api.get('/analytics/followups'),
    ]).then(([d, t, f]) => {
      setData(d.data);
      setByType(t.data);
      setFollowups(f.data);
    });
    api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
    api.get('/comms/campaigns-search', { params: { status: 'sent', limit: 100 } }).then((r) => setCampaigns(r.data.items)).catch(() => {});
  }, []);
  if (!data || !byType || !followups) return <div className="text-slate-500 text-sm">Loading...</div>;

  const funnel = data.conversion_funnel.map((s, i) => ({ ...s, name: STATUS_LABELS[s.stage] || s.stage, fill: COLORS[i % COLORS.length] }));
  const typeRows = byType.rows.map((r, i) => ({
    name: PARTNERSHIP_TYPE_LABELS[r.partnership_type] || r.partnership_type,
    revenue: r.revenue,
    pipeline: r.pipeline_value,
    closed: r.closed_won,
    conv: r.conversion_rate,
    outreach: r.outreach,
    avg: r.avg_deal,
    fill: COLORS[i % COLORS.length],
  }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="bg-gradient-to-br from-navy to-navy/95 rounded-2xl p-6 text-white shadow-lg flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-magenta text-[11px] font-semibold tracking-[0.12em] uppercase">Total Partnership Revenue</div>
          <div className="font-heading text-4xl md:text-5xl font-bold mt-1">{fmtCurrency(byType.total_revenue)}</div>
          <div className="text-sm text-white/60 mt-2">Across all partnership types (closed won, all-time)</div>
        </div>
      </div>

      <HistoricalAnalytics users={users} campaigns={campaigns} />

      {/* Follow-Up Metrics Panel */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="followup-metrics">
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider font-bold text-magenta">Follow-Up Performance</div>
          <h3 className="font-heading text-xl font-bold text-navy">Follow-Up Activity Metrics</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Due Today', value: followups.due_today, color: 'text-magenta' },
            { label: 'Due This Week', value: followups.due_this_week, color: 'text-navy' },
            { label: 'Overdue', value: followups.overdue, color: 'text-rose-600' },
            { label: 'Completed (30d)', value: followups.completed_30d, color: 'text-emerald-600' },
            { label: 'Completion Rate', value: `${followups.completion_rate}%`, color: 'text-navy' },
            { label: 'Avg Days Between', value: followups.avg_days_between ?? '—', color: 'text-slate-600' },
          ].map((m) => (
            <div key={m.label} className="bg-slate-50 rounded-xl p-4" data-testid={`followup-metric-${m.label.toLowerCase().replace(/\W/g, '-')}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{m.label}</div>
              <div className={`font-heading text-2xl font-bold mt-1 ${m.color}`}>{m.value}</div>
            </div>
          ))}
        </div>
        {followups.by_member?.length > 0 && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">Follow-Up Performance by Team Member (30d)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={followups.by_member}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="full_name" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} />
                <Tooltip />
                <Bar dataKey="completed" fill={MAGENTA} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Revenue by Partnership Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={typeRows} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} />
              <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} width={140} />
              <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} />
              <Bar dataKey="revenue" radius={[0, 8, 8, 0]}>
                {typeRows.map((r, i) => <Cell key={i} fill={r.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Pipeline Value by Partnership Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={typeRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} angle={-15} textAnchor="end" height={70} />
              <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => fmtCurrency(v)} />
              <Bar dataKey="pipeline" fill={MAGENTA} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Closures by Partnership Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={typeRows.filter((r) => r.closed > 0)} dataKey="closed" nameKey="name" outerRadius={100} label>
                {typeRows.map((r, i) => <Cell key={i} fill={r.fill} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Conversion Rate by Partnership Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={typeRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} angle={-15} textAnchor="end" height={70} />
              <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="conv" fill={NAVY} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Outreach Activity by Partnership Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={typeRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} angle={-15} textAnchor="end" height={70} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip />
              <Bar dataKey="outreach" fill={MAGENTA} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Average Deal Size by Partnership Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={typeRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} angle={-15} textAnchor="end" height={70} />
              <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => fmtCurrency(v)} />
              <Bar dataKey="avg" fill={NAVY} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Conversion Funnel</h3>
          <ResponsiveContainer width="100%" height={300}>
            <FunnelChart>
              <Tooltip />
              <Funnel dataKey="count" data={funnel} isAnimationActive>
                <LabelList position="right" fill="#0E1944" stroke="none" dataKey="name" fontSize={11} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Outreach by Method</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.outreach_by_method}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="method" stroke="#94A3B8" fontSize={11} tickFormatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" fill={MAGENTA} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
